import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeftIcon,
  SparklesIcon,
  ArrowPathIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  DocumentTextIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { SparklesIcon as SparklesSolidIcon } from '@heroicons/react/24/solid';
import { Card, CardBody, CardHeader } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDate } from '@/utils/formatters';
import toast from 'react-hot-toast';
import {
  getBlogAuthorWithStats,
  updateBlogAuthor,
  refreshBlogAuthor,
  getPostsByAuthor,
  bulkReassignAuthor,
  getAllBlogAuthors,
} from '@/services/blogApi';
import type { BlogAuthorWithStats, BlogAuthor, AuthorPostSummary, BlogStatus } from '@/types/blog';

const STATUS_BADGES: Record<BlogStatus, { variant: 'gray' | 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
  draft: { variant: 'gray', label: 'Draft' },
  in_review: { variant: 'warning', label: 'In Review' },
  published: { variant: 'success', label: 'Published' },
  archived: { variant: 'info', label: 'Archived' },
};

export default function BlogAuthorDetail() {
  const { authorId } = useParams<{ authorId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [expertise, setExpertise] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Posts state
  const [postsPage, setPostsPage] = useState(1);
  const [selectedPosts, setSelectedPosts] = useState<string[]>([]);

  // Modals
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [refreshExpertise, setRefreshExpertise] = useState('');
  const [keepExpertise, setKeepExpertise] = useState(true);
  const [targetAuthorId, setTargetAuthorId] = useState('');

  // Fetch author
  const { data: author, isLoading } = useQuery({
    queryKey: ['blogAuthor', authorId],
    queryFn: () => getBlogAuthorWithStats(authorId!),
    enabled: !!authorId,
  });

  // Fetch author's posts
  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ['authorPosts', authorId, postsPage],
    queryFn: () => getPostsByAuthor(authorId!, postsPage, 10),
    enabled: !!authorId,
  });

  // Fetch all authors for reassignment dropdown
  const { data: allAuthors } = useQuery({
    queryKey: ['allBlogAuthors'],
    queryFn: getAllBlogAuthors,
  });

  // Initialize form when author loads
  useEffect(() => {
    if (author) {
      setName(author.name);
      setBio(author.bio);
      setExpertise((author.expertise || []).join(', '));
      setIsActive(author.is_active);
      setHasChanges(false);
    }
  }, [author]);

  // Track changes
  useEffect(() => {
    if (author) {
      const expertiseChanged = expertise !== (author.expertise || []).join(', ');
      const changed =
        name !== author.name ||
        bio !== author.bio ||
        expertiseChanged ||
        isActive !== author.is_active;
      setHasChanges(changed);
    }
  }, [name, bio, expertise, isActive, author]);

  // Helper to generate slug from name
  const generateSlug = (authorName: string) => {
    return authorName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!authorId) throw new Error('No author ID');
      const expertiseArray = expertise
        .split(',')
        .map(e => e.trim().toLowerCase().replace(/\s+/g, '-'))
        .filter(e => e.length > 0);

      // Generate new slug if name changed
      const updates: Parameters<typeof updateBlogAuthor>[1] = {
        name,
        bio,
        expertise: expertiseArray,
        is_active: isActive,
      };

      // If name changed, update slug too
      if (author && name !== author.name) {
        updates.slug = generateSlug(name);
      }

      return updateBlogAuthor(authorId, updates);
    },
    onSuccess: () => {
      toast.success('Author updated');
      queryClient.invalidateQueries({ queryKey: ['blogAuthor', authorId] });
      queryClient.invalidateQueries({ queryKey: ['blogAuthors'] });
      setHasChanges(false);
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to update author';
      toast.error(message);
    },
  });

  // Refresh mutation
  const refreshMutation = useMutation({
    mutationFn: async () => {
      if (!authorId) throw new Error('No author ID');
      return refreshBlogAuthor({
        author_id: authorId,
        keep_expertise: keepExpertise,
        expertise: keepExpertise ? undefined : refreshExpertise.trim(),
      });
    },
    onSuccess: (data) => {
      if (data.success && data.author) {
        toast.success('Author refreshed');
        setName(data.author.name);
        setBio(data.author.bio);
        if (!keepExpertise) {
          setExpertise(data.author.expertise.join(', '));
        }
        queryClient.invalidateQueries({ queryKey: ['blogAuthor', authorId] });
        queryClient.invalidateQueries({ queryKey: ['blogAuthors'] });
        setShowRefreshModal(false);
      } else {
        toast.error(data.error || 'Failed to refresh author');
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to refresh author';
      toast.error(message);
    },
  });

  // Reassign posts mutation
  const reassignMutation = useMutation({
    mutationFn: async () => {
      if (!targetAuthorId || selectedPosts.length === 0) {
        throw new Error('Select posts and target author');
      }
      return bulkReassignAuthor(selectedPosts, targetAuthorId);
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`Reassigned ${data.updated_count} posts`);
        queryClient.invalidateQueries({ queryKey: ['authorPosts', authorId] });
        queryClient.invalidateQueries({ queryKey: ['blogAuthor', authorId] });
        queryClient.invalidateQueries({ queryKey: ['blogAuthors'] });
        setSelectedPosts([]);
        setShowReassignModal(false);
        setTargetAuthorId('');
      } else {
        toast.error(data.error || 'Failed to reassign posts');
      }
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to reassign posts';
      toast.error(message);
    },
  });

  const postColumns = useMemo<ColumnDef<AuthorPostSummary>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
            }}
          >
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              readOnly
              className="rounded border-gray-300 cursor-pointer"
              aria-label="Select all posts"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              row.toggleSelected(!row.getIsSelected());
            }}
          >
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              readOnly
              className="rounded border-gray-300 cursor-pointer"
              aria-label={`Select post ${row.original.title}`}
            />
          </div>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <div className="max-w-md">
            <button
              type="button"
              onClick={() => navigate(`/blog/${row.original.id}`)}
              className="font-medium text-gray-900 truncate hover:text-brand hover:underline text-left"
            >
              {row.original.title}
            </button>
            <div className="text-sm text-gray-500 truncate">
              /{row.original.slug}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          const badge = STATUS_BADGES[status];
          return (
            <Badge variant={badge.variant} dot>
              {badge.label}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'category_slug',
        header: 'Category',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600 capitalize">
            {row.original.category_slug?.replace(/-/g, ' ') || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'view_count',
        header: 'Views',
        cell: ({ row }) => (
          <span className="text-sm font-medium">
            {row.original.view_count.toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
    ],
    [navigate]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!author) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Author not found</h2>
        <Button
          variant="secondary"
          onClick={() => navigate('/blog-authors')}
          className="mt-4"
        >
          Back to Authors
        </Button>
      </div>
    );
  }

  const otherAuthors = (allAuthors || []).filter(a => a.id !== authorId && a.is_active);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/blog-authors')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{author.name}</h1>
              {author.ai_generated && (
                <SparklesSolidIcon className="w-5 h-5 text-purple-500" title="AI Generated" />
              )}
              <Badge variant={author.is_active ? 'success' : 'gray'} dot>
                {author.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-sm text-gray-500">
              Slug: <span className="font-mono">{author.slug || author.id}</span>
              {author.slug && author.slug !== author.id && (
                <span className="ml-2 text-gray-400">(ID: {author.id})</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setRefreshExpertise((author.expertise || []).join(', '));
              setKeepExpertise(true);
              setShowRefreshModal(true);
            }}
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          >
            Refresh with AI
          </Button>
          <Button
            onClick={() => updateMutation.mutate()}
            disabled={!hasChanges}
            loading={updateMutation.isPending}
            leftIcon={<CheckIcon className="w-4 h-4" />}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total Posts</p>
            <p className="text-2xl font-bold">{author.total_posts}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Published</p>
            <p className="text-2xl font-bold text-green-600">{author.published_posts}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total Views</p>
            <p className="text-2xl font-bold text-blue-600">
              {author.total_views.toLocaleString()}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="py-4">
            <p className="text-sm text-gray-500">Total Shares</p>
            <p className="text-2xl font-bold text-purple-600">
              {author.total_shares.toLocaleString()}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Edit Form */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Author Details</h2>
        </CardHeader>
        <CardBody>
          <div className="grid gap-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="author-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  id="author-name"
                  name="authorName"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  aria-label="Author name"
                />
              </div>
              <div>
                <label htmlFor="author-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="author-status"
                  name="authorStatus"
                  value={isActive ? 'active' : 'inactive'}
                  onChange={(e) => setIsActive(e.target.value === 'active')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  aria-label="Author status"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="author-bio" className="block text-sm font-medium text-gray-700 mb-1">
                Bio
              </label>
              <textarea
                id="author-bio"
                name="authorBio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label="Author bio"
              />
            </div>

            <div>
              <label htmlFor="author-expertise" className="block text-sm font-medium text-gray-700 mb-1">
                Expertise Areas
              </label>
              <input
                id="author-expertise"
                name="authorExpertise"
                type="text"
                value={expertise}
                onChange={(e) => setExpertise(e.target.value)}
                placeholder="e.g., plumbing, home-services, contractor-selection"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                aria-label="Expertise areas"
              />
              <p className="mt-1 text-xs text-gray-500">
                Comma-separated list of expertise areas (used for auto-assignment)
              </p>
            </div>

            {author.ai_generation_prompt && (
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm font-medium text-purple-700">AI Generation Prompt</p>
                <p className="text-sm text-purple-600 mt-1">{author.ai_generation_prompt}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Posts Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold">Assigned Posts</h2>
              <span className="text-sm text-gray-500">({postsData?.total || 0})</span>
            </div>
            {selectedPosts.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {selectedPosts.length} selected
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowReassignModal(true)}
                  leftIcon={<UserGroupIcon className="w-4 h-4" />}
                >
                  Reassign to Another Author
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody padding="none">
          <DataTable
            data={postsData?.posts || []}
            columns={postColumns}
            isLoading={postsLoading}
            page={postsPage}
            pageSize={10}
            totalCount={postsData?.total}
            onPageChange={setPostsPage}
            getRowId={(row) => row.id}
            onRowSelectionChange={(rows) => {
              setSelectedPosts(rows.map((r) => r.original.id));
            }}
          />
        </CardBody>
      </Card>

      {/* Refresh Modal */}
      <Modal
        isOpen={showRefreshModal}
        onClose={() => {
          if (!refreshMutation.isPending) {
            setShowRefreshModal(false);
          }
        }}
        title="Refresh Author with AI"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Generate a new name and bio for this author using AI. You can choose
            to keep the existing expertise areas or generate new ones.
          </p>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="keepExpertise"
              name="keepExpertise"
              checked={keepExpertise}
              onChange={(e) => setKeepExpertise(e.target.checked)}
              className="rounded border-gray-300"
              disabled={refreshMutation.isPending}
              aria-label="Keep existing expertise areas"
            />
            <label htmlFor="keepExpertise" className="text-sm text-gray-700">
              Keep existing expertise areas
            </label>
          </div>

          {!keepExpertise && (
            <div>
              <label htmlFor="refresh-expertise" className="block text-sm font-medium text-gray-700 mb-1">
                New Expertise Areas
              </label>
              <input
                id="refresh-expertise"
                name="refreshExpertise"
                type="text"
                value={refreshExpertise}
                onChange={(e) => setRefreshExpertise(e.target.value)}
                placeholder="e.g., plumbing, home-services"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
                disabled={refreshMutation.isPending}
                aria-label="New expertise areas"
              />
            </div>
          )}

          {refreshMutation.isPending && (
            <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
              <ArrowPathIcon className="w-5 h-5 text-purple-600 animate-spin" />
              <span className="text-purple-700">Generating new author details...</span>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => setShowRefreshModal(false)}
            disabled={refreshMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => refreshMutation.mutate()}
            loading={refreshMutation.isPending}
            leftIcon={<SparklesIcon className="w-4 h-4" />}
          >
            Refresh
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reassign Modal */}
      <Modal
        isOpen={showReassignModal}
        onClose={() => {
          if (!reassignMutation.isPending) {
            setShowReassignModal(false);
            setTargetAuthorId('');
          }
        }}
        title="Reassign Posts"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Move <strong>{selectedPosts.length}</strong> selected post(s) to another author.
          </p>

          <div>
            <label htmlFor="reassign-target-author" className="block text-sm font-medium text-gray-700 mb-1">
              Target Author
            </label>
            <select
              id="reassign-target-author"
              name="targetAuthor"
              value={targetAuthorId}
              onChange={(e) => setTargetAuthorId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
              disabled={reassignMutation.isPending}
              aria-label="Target author"
            >
              <option value="">Select an author...</option>
              {otherAuthors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.expertise?.slice(0, 2).join(', ') || 'no expertise'})
                </option>
              ))}
            </select>
          </div>

          {otherAuthors.length === 0 && (
            <div className="p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-700">
                No other active authors available. Create a new author first.
              </p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowReassignModal(false);
              setTargetAuthorId('');
            }}
            disabled={reassignMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => reassignMutation.mutate()}
            loading={reassignMutation.isPending}
            disabled={!targetAuthorId}
          >
            Reassign Posts
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
